import { useMemo, useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import useEmblaCarousel from "embla-carousel-react";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "@/components/ui/use-toast";
import {
  Clock,
  Flame,
  Dumbbell,
  ChevronDown,
  ArrowLeft,
  Info,
  RotateCcw,
  CheckCircle2,
  Loader2,
  Trash2,
} from "lucide-react";
import ExerciseDetailsModal from "@/components/dashboard/ExerciseDetailsModal";
import { getWorkoutImage } from "@/utils/workoutImages";

/**
 * LibraryPlanDetail — swipeable day-by-day view of a single WorkoutTemplate.
 *
 * Layout mirrors Dashboard `TodaysWorkout`:
 *   - Each day is a slide inside an Embla carousel. Page indicator is a row
 *     of *dots* (active one fattens to a pill) instead of full day-name
 *     pills — keeps the chrome quiet so the focus stays on the exercises.
 *   - Exercises are accordion rows: tap the row → expand into a card with
 *     the gif on the left + sets/reps/rest/info-button on the right. Only
 *     one row open at a time so the page stays scannable.
 *   - Sticky "Select this Plan" CTA at the bottom hits the new
 *     `/api/library/{id}/assign` endpoint which materialises the template
 *     into the caller's weekly schedule (one WorkoutPlan per day +
 *     UserWeeklyWorkoutPlan upsert + history snapshot) — see the backend
 *     route for the day-mapping logic.
 *
 * TopBar swap (scan → back) is handled in `TopBar.jsx` via a `matchPath`.
 */

const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

function daySortKey(label) {
  const lower = (label || "").toLowerCase();
  const idx = DAY_ORDER.indexOf(lower);
  if (idx >= 0) return idx;
  const m = lower.match(/day\s*(\d+)/);
  if (m) return 100 + Number(m[1]);
  return 200;
}

function ExerciseRow({ ex, idx, isOpen, onToggle, fullExercise }) {
  const gif = ex.gifUrl || ex.gif_url || fullExercise?.gifUrl || fullExercise?.gif_url || fullExercise?.video_url || null;
  const [modalOpen, setModalOpen] = useState(false);
  const setsLabel = ex.sets ? `${ex.sets} Sets` : "Sets";
  const repsParts = ex.sets ? Array.from({ length: ex.sets }, () => (ex.reps ? String(ex.reps) : "?")) : [];

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={onToggle}
        data-testid={`library-exercise-${idx}`}
        className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl text-left transition bg-[#111B2E] border border-[#1E293B] hover:bg-[#152038]"
      >
        <p className="flex-1 text-base font-bold capitalize truncate">
          {idx + 1}. {ex.name}
        </p>
        <ChevronDown className="w-5 h-5 text-white/40 shrink-0" />
      </button>
    );
  }

  return (
    <>
      <div
        data-testid={`library-exercise-${idx}`}
        className="rounded-xl p-3 bg-[#0F1A2E] border border-sky-400/40 shadow-[0_0_0_1px_rgba(56,189,248,0.15),0_0_20px_-8px_rgba(56,189,248,0.4)]"
      >
        <div className="flex gap-3.5">
          <button
            type="button"
            onClick={onToggle}
            className="shrink-0"
            aria-label="Collapse exercise"
          >
            {gif ? (
              <img
                src={gif}
                alt={ex.name}
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-lg object-cover bg-black"
              />
            ) : (
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-lg bg-black/60 flex items-center justify-center">
                <Dumbbell className="w-7 h-7 text-white/30" />
              </div>
            )}
          </button>

          <div className="flex-1 min-w-0 relative">
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              aria-label="Exercise instructions"
              data-testid={`library-exercise-info-${idx}`}
              className="absolute top-0 right-0 w-6 h-6 rounded-full flex items-center justify-center bg-white/10 text-white/70 hover:text-white hover:bg-white/20 transition"
            >
              <Info className="w-3.5 h-3.5" />
            </button>

            <p className="text-lg font-bold capitalize pr-8 leading-tight">
              {idx + 1}. {ex.name}
            </p>

            <p className="text-sky-400 text-sm font-semibold mt-2">
              {setsLabel}
              {repsParts.length > 0 && ` (${repsParts.join(", ")} Reps)`}
            </p>
            {ex.rest_seconds ? (
              <p className="text-sky-400/80 text-xs mt-0.5 inline-flex items-center gap-1">
                <RotateCcw className="w-3 h-3" /> {ex.rest_seconds}s rest
              </p>
            ) : null}
            {ex.notes ? (
              <p className="mt-2 text-[12px] text-white/55 italic">{ex.notes}</p>
            ) : null}
          </div>
        </div>
      </div>
      {modalOpen && (
        <ExerciseDetailsModal
          exercise={{
            ...fullExercise,
            ...ex,
            gifUrl: gif,
            instructions: fullExercise?.instructions || ex.instructions,
          }}
          open={modalOpen}
          onOpenChange={setModalOpen}
        />
      )}
    </>
  );
}

export default function LibraryPlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: plan, isLoading, isError } = useQuery({
    queryKey: ["workoutTemplate", id],
    queryFn: async () => {
      const all = await base44.entities.WorkoutTemplate.list();
      return all.find((t) => t.id === id) || null;
    },
    enabled: !!id,
  });

  // Pull the global exercise catalogue once so we can hydrate each template
  // exercise with a `gifUrl` (templates store only names + sets/reps).
  const { data: allExercises = [] } = useQuery({
    queryKey: ["exercises"],
    queryFn: () => base44.entities.Exercises.list("name", 2500),
  });
  const exerciseByName = useMemo(() => {
    const map = new Map();
    for (const e of allExercises) {
      if (e?.name) map.set(e.name.trim().toLowerCase(), e);
    }
    return map;
  }, [allExercises]);

  const days = useMemo(() => {
    if (!plan) return [];
    // Build `[dayKey, exercises[], dayLabel|null]` triples. `day_label` is
    // an optional free-form name like "Leg Day" / "Push Day" — when set
    // on any exercise in a bucket, that label wins for the whole day.
    const map = new Map();
    const labelByDay = new Map();
    for (const ex of plan.exercises || []) {
      const key = (ex.day || "").trim() || "Day 1";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(ex);
      if (ex.day_label && !labelByDay.has(key)) {
        labelByDay.set(key, String(ex.day_label));
      }
    }
    if (map.size <= 1 && plan.days_per_week > 1) {
      const synthetic = new Map();
      const exs = plan.exercises || [];
      const per = Math.ceil(exs.length / plan.days_per_week) || 1;
      for (let i = 0; i < plan.days_per_week; i++) {
        synthetic.set(`Day ${i + 1}`, exs.slice(i * per, (i + 1) * per));
      }
      return [...synthetic.entries()].map(([k, v]) => [k, v, null]);
    }
    return [...map.entries()]
      .sort((a, b) => daySortKey(a[0]) - daySortKey(b[0]))
      .map(([k, v]) => [k, v, labelByDay.get(k) || null]);
  }, [plan]);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start" });
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Accordion: which exercise is open within the *current* day. We reset
  // when the user swipes to a new day so the next day starts fully
  // collapsed, matching Dashboard `TodaysWorkout` behaviour.
  const [openIdx, setOpenIdx] = useState(-1);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
      setOpenIdx(-1);
    };
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const assignMutation = useMutation({
    mutationFn: async () => {
      // Reuses the same access-token key the rest of the app reads
      // (base44Client → TOKEN_KEY = "ta_access_token").
      const token = localStorage.getItem("ta_access_token");
      const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const res = await fetch(`${base}/api/library/${id}/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["userWeeklyWorkoutPlan"] });
      queryClient.invalidateQueries({ queryKey: ["workouts"] });
      queryClient.invalidateQueries({ queryKey: ["weeklyWorkoutHistory"] });
      const dayCount = (data?.created_plans || 0) + (data?.reused_plans || 0);
      const dayWord = dayCount === 1 ? "1 training day is" : `${dayCount} training days are`;
      toast({
        title: "Plan assigned to your week",
        description: `${dayWord} now in your weekly schedule.`,
      });
      navigate("/");
    },
    onError: (err) => {
      toast({
        title: "Could not assign plan",
        description: err.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  // Detect whether THIS template is currently sitting in the user's
  // weekly schedule so the CTA can switch between "Select this Plan"
  // (default) and "Remove from my Week" (when active). We reuse the same
  // queries the Library index page uses so cache hits are likely.
  const { data: weeklyWorkoutPlan = null } = useQuery({
    queryKey: ["userWeeklyWorkoutPlan", user?.id],
    queryFn: () =>
      base44.entities.UserWeeklyWorkoutPlan.filter({ user_id: user.id }).then(
        (r) => r[0] || null,
      ),
    enabled: !!user?.id,
  });
  const { data: userWorkoutPlans = [] } = useQuery({
    queryKey: ["workouts"],
    queryFn: () => base44.entities.WorkoutPlan.list("-created_date"),
    enabled: !!user?.id,
  });
  const isActive = useMemo(() => {
    if (!weeklyWorkoutPlan || !id) return false;
    const DAYS = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const dayPlanIds = new Set();
    for (const d of DAYS) {
      const pid = weeklyWorkoutPlan[d];
      if (pid) dayPlanIds.add(pid);
    }
    if (dayPlanIds.size === 0) return false;
    const prefix = `library:${id}:`;
    for (const wp of userWorkoutPlans) {
      if (dayPlanIds.has(wp.id) && (wp.external_id || "").startsWith(prefix)) {
        return true;
      }
    }
    return false;
  }, [weeklyWorkoutPlan, userWorkoutPlans, id]);

  const removeMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem("ta_access_token");
      const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const res = await fetch(`${base}/api/library/${id}/assign`, {
        method: "DELETE",
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["userWeeklyWorkoutPlan"] });
      queryClient.invalidateQueries({ queryKey: ["workouts"] });
      queryClient.invalidateQueries({ queryKey: ["weeklyWorkoutHistory"] });
      const cleared = (data?.cleared_days || []).length;
      toast({
        title: cleared > 0 ? "Removed from your week" : "Already not in your week",
        description:
          cleared > 0
            ? `${cleared} day${cleared === 1 ? "" : "s"} cleared from your schedule.`
            : "Nothing to remove.",
      });
      navigate("/library");
    },
    onError: (err) => {
      toast({
        title: "Could not remove plan",
        description: err.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return <p className="text-sm text-white/60" data-testid="library-detail-loading">Loading plan…</p>;
  }
  if (isError || !plan) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="library-detail-not-found">
        <p className="text-sm text-white/70">Plan not found.</p>
        <Link to="/library" className="text-sky-400 text-sm mt-2 inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to Library
        </Link>
      </div>
    );
  }

  const totalExercises = (plan.exercises || []).length;

  return (
    <div className="space-y-5 pb-4" data-testid="library-detail-page">
      {/* Hero band — plan name + meta. The TopBar has the back button. */}
      <div className="relative rounded-2xl overflow-hidden border border-sky-500/25 p-5 sm:p-6 bg-[linear-gradient(115deg,#02061A_0%,#082351_55%,#1E40AF_100%)]">
        {/* Cinematic Background Image */}
        <div className="absolute inset-0 pointer-events-none opacity-30 overflow-hidden">
          <img
            src={getWorkoutImage(plan)}
            alt=""
            className="w-full h-full object-cover filter blur-sm scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#02061A] via-[#02061A]/70 to-transparent" />
        </div>

        <div className="relative z-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-sky-300/80">
            {plan.goal?.replace(/_/g, " ") || "training plan"}
          </p>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl leading-tight mt-1.5">
            {plan.name}
          </h1>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-semibold uppercase tracking-wider">
              <Flame className="w-3 h-3" /> {plan.difficulty || "all levels"}
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-semibold uppercase tracking-wider">
              <Clock className="w-3 h-3" /> {plan.days_per_week} days / wk
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-semibold uppercase tracking-wider">
              <Dumbbell className="w-3 h-3" /> {totalExercises} exercises
            </span>
          </div>
          {plan.description ? (
            <p className="mt-3 text-sm text-white/75 leading-relaxed">{plan.description}</p>
          ) : null}
        </div>
      </div>

      {/* Day dots — replaces the previous pill row. Tap any dot to jump
          straight to that day (Embla's `scrollTo`). The active dot widens
          into a pill so it's obvious which page you're on. */}
      {days.length > 1 && (
        <div
          className="flex items-center justify-center gap-2"
          data-testid="library-day-dots"
        >
          {days.map(([label], idx) => {
            const isActive = idx === selectedIndex;
            return (
              <button
                key={label}
                type="button"
                onClick={() => emblaApi?.scrollTo(idx)}
                aria-label={`Go to ${label}`}
                data-testid={`library-day-dot-${idx}`}
                className={`h-2.5 rounded-full transition-all ${
                  isActive
                    ? "w-8 bg-sky-400 shadow-[0_0_12px_-2px_rgba(56,189,248,0.8)]"
                    : "w-2.5 bg-white/25 hover:bg-white/45"
                }`}
              />
            );
          })}
        </div>
      )}

      {/* Carousel with one slide per day */}
      <div ref={emblaRef} className="overflow-hidden" data-testid="library-day-carousel">
        <div className="flex">
          {days.map(([label, exs, dayLabel], dayIdx) => (
            <div
              key={label}
              className="shrink-0 grow-0 basis-full"
              data-testid={`library-day-slide-${dayIdx}`}
            >
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center justify-between mb-3 gap-3">
                  <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
                    <p className="font-display font-bold text-lg capitalize truncate">{label}</p>
                    {dayLabel && (
                      <span
                        data-testid={`library-day-label-${dayIdx}`}
                        className="text-sm font-semibold text-sky-300 truncate"
                      >
                        · {dayLabel}
                      </span>
                    )}
                  </div>
                  <span className="shrink-0 text-[11px] text-white/50">
                    {exs.length} exercise{exs.length === 1 ? "" : "s"}
                  </span>
                </div>
                {exs.length === 0 ? (
                  <p className="text-sm text-white/55">No exercises tagged for this day.</p>
                ) : (
                  <div className="space-y-2.5">
                    {exs.map((ex, i) => (
                      <ExerciseRow
                        key={`${dayIdx}-${ex.name}-${i}`}
                        ex={ex}
                        idx={i}
                        isOpen={dayIdx === selectedIndex && openIdx === i}
                        onToggle={() => {
                          // Toggle within the active day only — opening an
                          // exercise on a non-active slide while it's off
                          // screen would be confusing.
                          if (dayIdx === selectedIndex) {
                            setOpenIdx((cur) => (cur === i ? -1 : i));
                          }
                        }}
                        fullExercise={exerciseByName.get((ex.name || "").toLowerCase())}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Static CTA — toggles between "Select this Plan" (default) and
          "Remove from my Week" when at least one day of this template is
          already in the user's weekly schedule. Same position so the
          muscle-memory stays consistent. */}
      {isActive ? (
        <button
          type="button"
          onClick={() => removeMutation.mutate()}
          disabled={removeMutation.isPending}
          data-testid="library-remove-plan-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-2xl text-base font-bold bg-rose-500 text-white shadow-[0_8px_24px_-8px_rgba(244,63,94,0.7)] hover:bg-rose-400 active:scale-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {removeMutation.isPending ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Removing…
            </>
          ) : (
            <>
              <Trash2 className="w-5 h-5" /> Remove from my Week
            </>
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => assignMutation.mutate()}
          disabled={assignMutation.isPending || !user}
          data-testid="library-select-plan-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-2xl text-base font-bold bg-sky-500 text-white shadow-[0_8px_24px_-8px_rgba(56,189,248,0.7)] hover:bg-sky-400 active:scale-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {assignMutation.isPending ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" /> Assigning…
            </>
          ) : (
            <>
              <CheckCircle2 className="w-5 h-5" /> Select this Plan
            </>
          )}
        </button>
      )}
    </div>
  );
}
